

import React, { useEffect, useRef, useState } from 'react';
import { useFormik, FormikProps } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

import { FormControl, InputLabel, Select, MenuItem, Grid, Typography, Autocomplete, Box, Checkbox } from '@mui/material';
import { toast } from 'react-toastify';
import { createOpenProjectTask, getOpenProjectAssignee } from '../../services/openProject.service';
import RecurringTaskForm from '../recurringTaskForm';
import './style.scss'


interface DialogProps {
  isOpen: boolean;
  onClose: (isRefetch?: boolean) => void;
  isSubTask?: boolean;
}

const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

const getCurrentDate = () => moment().format('YYYY-MM-DD');

const OpenProjectCreateTaskDialog: React.FC<DialogProps> = ({ isOpen = false, onClose, isSubTask = false }) => {

  const issueDetails = useSelector((state: RootState) => state.issue);
  const userState = useSelector((state: RootState) => state.user);
  const userDetails = userState?.user;
  const userLink = userDetails?.openProjectDetails?._links?.self?.href;

  const projectState = useSelector((state: RootState) => state.project);
  const selectedProjectLink = projectState?.selectedProject?._links?.createWorkPackageImmediately?.href;
  const selectedProjectId = projectState?.selectedProject?.id || '';

  const [assigneeList, setAssigneeList] = useState<any>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('');

  const [loading, setLoading] = useState(false);


  const recurringInitialValues = {
    frequency: 'null',
    weeklyDays: [] as string[],
    monthlyType: "",
    monthlyDay: "",
    monthlyPosition: "",
    monthlySelectedDay: "",
    startDate: getCurrentDate(),
    endDateType: "",
    endDate: "",
    endAfterOccurrences: "",
    endMonth: "",
    endYear: "",
    repeatEvery: 1,
    excludedDates: [],
    priority: "medium",
    notes: "",
    customPattern: "",
    includeTime: false,
    time: "",
    platform: "OPEN_PROJECT"
  }

  const initialValues = {
    assignee: {},
    taskName: '',
    description: '',
    dueDate: getCurrentDate(),
    startDate: getCurrentDate(),
    timeEstimation: {
      days: "",
      hours: "",
      minutes: "30"
    },
    recurringLabel: false,
    recurringForm: recurringInitialValues

  };

  const validationSchema = Yup.object({
    taskName: Yup.string().required('Task Name is required'),
    description: Yup.string(),
    dueDate: Yup.date()
      .nullable()
      .required('Due date is required')
      .when('startDate', (startDate, schema) => {
        return startDate[0]
          ? schema.min(startDate, 'Due date cannot be earlier than start date')
          : schema;
      }),
    startDate: Yup.date()
      .nullable(),

    timeEstimation: Yup.object({
      days: Yup.number(),
      hours: Yup.number(),
      minutes: Yup.number(),
    }).test('at-least-one', 'At least one field (days, hours, or minutes) must be filled', (value: any) => {
      return value && (value.days || value.hours || value.minutes);
    }),
    assignee: Yup.string().required('Assignee is required'),
    recurringLabel: Yup.boolean(),
    recurringForm: Yup.object({
      frequency: Yup.string().required("Frequency is required"),
      weeklyDays: Yup.array()
        .of(Yup.string())
        .when('frequency', (frequency, schema) => {
          if (frequency?.length && frequency[0] === 'weekly') {
            return schema.min(1, 'Select at least one day for weekly frequency');
          }
          return schema;
        }),
      monthlyDay: Yup.number()
        .nullable()
        .when('monthlyType', (monthlyType, schema) => {
          if (monthlyType?.length && monthlyType[0] === 'day') {
            return schema
              .min(1, 'Day must be at least 1')
              .max(31, 'Day must be at most 31').required('Day is required for monthly frequency');
          }
          return schema;
        }),
      monthlyType: Yup.string()
        .when('frequency', (frequency, schema) => {
          if (frequency?.length && frequency[0] === 'monthly') {
            return schema.required('Select a monthly pattern type');
          }
          return schema;
        }),
      monthlyPosition: Yup.string()

        .when('monthlyType', (monthlyType, schema) => {
          if (monthlyType?.length && monthlyType[0] === 'position') {
            return schema.required('Select position');
          }
          return schema;
        }),
      endDate: Yup.string()
        .when('endDateType', (endDateType, schema) => {
          if (endDateType?.length && endDateType[0] === 'date') {
            return schema.required('End date is required');
          }
          return schema;
        }),
      endAfterOccurrences: Yup.string()
        .when('endDateType', (endDateType, schema) => {
          if (endDateType?.length && endDateType[0] === 'occurrences') {
            return schema.required('Occurrence is required');
          }
          return schema;
        })
        .when('frequency', (frequency, schema) => {
          if (frequency?.length && frequency[0] === 'yearly') {
            return schema.required('Occurrence is required');
          }
          return schema
        }),

      endMonth: Yup.string()
        .when('endDateType', (endDateType, schema) => {
          if (endDateType?.length && endDateType[0] === 'monthYear') {
            return schema.required('Month is required');
          }
          return schema;
        })
        .when('frequency', (frequency, schema) => {
          if (frequency?.length && frequency[0] === 'monthly') {
            return schema.required('Month is required');
          }
          return schema
        }),


      endYear: Yup.string()
        .when('endDateType', (endDateType, schema) => {
          if (endDateType?.length && endDateType[0] === 'monthYear') {
            return schema.required('End year is required');
          }
          return schema;
        })
        .when('frequency', (frequency, schema) => {
          if (frequency?.length && frequency[0] === 'monthly') {
            return schema.required('Month is required');
          }
          return schema
        }),

      monthlySelectedDay: Yup.string()
        .when('monthlyType', (monthlyType, schema) => {
          if (monthlyType?.length && monthlyType[0] === 'position') {
            return schema.required('Select day');
          }
          return schema;
        })
        .when('monthlyType', (monthlyType, schema) => {
          if (monthlyType?.length && monthlyType[0] === 'position') {
            return schema.required('Select day');
          }
          return schema;
        }),
      startDate: Yup.date().required("Start date is required"),
    }).when('recurringLabel', (recurringLabel, schema) => {
      if (recurringLabel?.length && recurringLabel[0]) {
        return schema.required('recurringLabel');
      }
      return schema;
    })
  });

  useEffect(() => {
    console.log("[TESTING] Formik Errors ", formik.errors.recurringForm)
  })

  const onSubmit = async (values: typeof initialValues) => {

    const selectedAssignee = assigneeList.filter((e: any) => e?.id === selectedAssigneeId) || [];

    // commanded for future
    const payload: Record<string, any> = {
      link: selectedProjectLink,
      subject: values.taskName,
      dueDate: values.dueDate,

      // description:values.description,
      description: {
        raw: values.description
      },
      startDate: values.startDate,
      // "estimatedTime": "PT3H",
      estimatedTime: values.timeEstimation,
      _links: {
        assignee: {
          href: selectedAssignee.length > 0 ? selectedAssignee[0]['_links']['self']['href'] : userLink
        },
        //   "responsible": {
        //     "href": "/api/v3/users/51"
        // },
        // "priority":{
        //     "href": "/api/v3/priorities/7"
        // },
        type: {
          href: isSubTask ? "/api/v3/types/9" : "/api/v3/types/1"
        }
      },

    };

    if (values.recurringForm) {
      payload.recurringTaskValues = values.recurringForm
    }

    if (isSubTask) {
      if (!issueDetails?.issueData?._links?.self?.href) {
        return toast.warn("Parent task not selected")
      }
      payload._links.parent = {
        href: issueDetails?.issueData?._links?.self?.href,
      }
      payload.link = issueDetails?.issueData?._links?.project?.href + "/work_packages"
    }

    const prefix = isSubTask ? 'Sub-Task' : 'Task';
    try {
      setLoading(true);
      let response = await createOpenProjectTask(payload);
      if (response?.statusCode === 200) {
        toast.success(`${prefix} created successfully`);
        formik.resetForm();
        onClose(true);
        setLoading(false);
      } else {
        toast.error(`${prefix} creation failed`);
        setLoading(false);
      }
    } catch (error) {
      toast.error(`${prefix} creation failed`);
      setLoading(false);
    }
  };

  const handleClose = () => {
    formik.resetForm();
    onClose(false)
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: true,
    onSubmit

  });

  // eslint-disable-next-line react-hooks/exhaustive-deps

  const resetRecurringForm = () => {

    formik.setFieldValue('recurringForm', recurringInitialValues)
  }

  const getOpenProjectUserList = async () => {
    let userData: any = [];
    setUserLoading(() => true);
    try {
      const taskProjectId = issueDetails.issueData?._links?.project?.href?.split('/')?.pop();
      const userDataResponse = await getOpenProjectAssignee(selectedProjectId || taskProjectId);
      if (userDataResponse?.data?._embedded?.elements) {
        userData = userDataResponse?.data?._embedded?.elements;
        userData = userData?.map((user: any) => ({ ...user, displayName: user.name }))
      }
    } catch (error) {
      userData = [];
    } finally {
      setUserLoading(() => false);
    }
    setAssigneeList(() => [...userData]);
    return userData;
  }

  useEffect(() => {
    getOpenProjectUserList();
  }, []);

  const getDefaultValue = () => {
    formik.setFieldValue("assignee", userDetails?.openProjectDetails?.name);
    setSelectedAssigneeId(userDetails?.openProjectDetails?.id || '');
    return {
      displayName: userDetails?.openProjectDetails?.name
    };
  }

  useEffect(() => {
    if (formik.values.startDate && formik.values.dueDate < formik.values.startDate) {
      formik.setFieldValue('dueDate', formik.values.startDate);
    }
  }, [formik.values.startDate]);

  return (
    <Dialog open={isOpen} className="create-openproject-task-dialog-container">
      <DialogTitle>Create {isSubTask ? "Sub" : ""}Task</DialogTitle>
      <DialogContent className="create-openproject-sub-task-dialog-content">
        <form onSubmit={formik.handleSubmit} className="dialog-openproject-form">
          <Autocomplete
            fullWidth
            id="select-assignee"
            options={assigneeList}
            loading={userLoading}
            autoHighlight
            defaultValue={getDefaultValue}
            onChange={(event, value: any) => {
              const selectedAccountId = value?.id || '';
              setSelectedAssigneeId(selectedAccountId);
              formik.setValues({
                ...formik.values,
                assignee: value?.displayName || ''
              })
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option: any) => option.displayName}
            renderOption={(props, option) => (
              <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props} key={option.id}>
                {option.displayName}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                required
                {...params}
                label="Assignee"
                name="assignee"
                value={formik.values.assignee}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'select-open-project-assignee',
                }}
                error={formik.touched.assignee && Boolean(formik.errors.assignee)}
                helperText={formik.touched.assignee && formik.errors.assignee && 'Assignee is required'}
              />
            )}
          />
          <TextField
            required
            label="Task Name"
            type="text"
            name="taskName"
            value={formik.values.taskName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.taskName && Boolean(formik.errors.taskName)}
            helperText={formik.touched.taskName && formik.errors.taskName}
            fullWidth
            margin="normal"
          />
          <Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Time Estimation
              </Typography>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <FormControl fullWidth error={Boolean(formik.errors.timeEstimation && formik.touched.timeEstimation)}>
                  <InputLabel className='bg-white' id="timeEstimation-days-label">Days</InputLabel>
                  <Select
                    labelId="timeEstimation-days-label"
                    id="timeEstimation.days"
                    name="timeEstimation.days"
                    value={formik.values.timeEstimation.days}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.timeEstimation?.days && Boolean(formik.errors.timeEstimation?.days)}
                  >
                    <MenuItem className='empty-select-item' style={{ height: '20px' }} value=""></MenuItem>
                    <MenuItem value="1">1</MenuItem>
                    <MenuItem value="2">2</MenuItem>
                    <MenuItem value="3">3</MenuItem>
                    <MenuItem value="4">4</MenuItem>
                    <MenuItem value="5">5</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={4}>
                <FormControl fullWidth error={Boolean(formik.errors.timeEstimation && formik.touched.timeEstimation)}>
                  <InputLabel className='bg-white' id="timeEstimation-hours-label">Hours</InputLabel>
                  <Select
                    labelId="timeEstimation-hours-label"
                    id="timeEstimation.hours"
                    name="timeEstimation.hours"
                    value={formik.values.timeEstimation.hours}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.timeEstimation?.hours && Boolean(formik.errors.timeEstimation?.hours)}
                  >
                    <MenuItem className='empty-select-item' style={{ height: '20px' }} value=""></MenuItem>
                    <MenuItem value="1">1</MenuItem>
                    <MenuItem value="2">2</MenuItem>
                    <MenuItem value="3">3</MenuItem>
                    <MenuItem value="4">4</MenuItem>
                    <MenuItem value="5">5</MenuItem>
                    <MenuItem value="6">6</MenuItem>
                    <MenuItem value="7">7</MenuItem>
                    <MenuItem value="8">8</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={4}>
                <FormControl fullWidth error={Boolean(formik.errors.timeEstimation && formik.touched.timeEstimation)}>
                  <InputLabel className='bg-white' style={{ background: 'white' }} id="timeEstimation-minutes-label"> Minutes </InputLabel>
                  <Select
                    defaultValue={formik.values.timeEstimation.minutes}
                    labelId="timeEstimation-minutes-label"
                    id="timeEstimation.minutes"
                    name="timeEstimation.minutes"
                    value={formik.values.timeEstimation.minutes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.timeEstimation?.minutes && Boolean(formik.errors.timeEstimation?.minutes)}
                  >
                    <MenuItem className='empty-select-item' style={{ height: '20px' }} value=""></MenuItem>
                    <MenuItem value="15">15</MenuItem>
                    <MenuItem value="30">30</MenuItem>
                    <MenuItem value="45">45</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <TextField
            label="Description"
            type="text"
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Start Date"
            type="date"
            name="startDate"
            value={formik.values.startDate || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.startDate && Boolean(formik.errors.startDate)}
            helperText={formik.touched.startDate && formik.errors.startDate}
            fullWidth
            margin="normal"
          />
          <TextField
            required
            label="Due Date"
            type="date"
            name="dueDate"
            value={formik.values.dueDate || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={(formik.touched.dueDate || formik.touched.startDate) && Boolean(formik.errors.dueDate)}
            helperText={(formik.touched.dueDate || formik.touched.startDate) && formik.errors.dueDate}
            fullWidth
            margin="normal"
          />
          <Typography variant="h6" gutterBottom>
            Does this task repeat?
            <Checkbox {...label} name="recurringLabel" value={formik.values.recurringLabel} onChange={formik.handleChange} onBlur={formik.handleBlur} />
          </Typography>
          {
            formik.values.recurringLabel && <RecurringTaskForm
              values={formik.values.recurringForm}
              errors={formik.errors.recurringForm || {}}
              touched={formik.touched.recurringForm || {}}
              setFieldValue={formik.setFieldValue}
              setFieldTouched={formik.setFieldTouched}
            />
          }
          <div className='action-button' style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }} >
            <Button variant="contained" color="secondary" onClick={handleClose} >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || (!formik.isValid && formik.dirty && (formik.values.recurringLabel ? ((formik.errors.recurringForm && formik.values.recurringLabel) ? formik.values.recurringLabel : true) : true))} variant="contained" color="primary">
              {loading ? "Creating..." : "Create"}
            </Button>

          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};


export default OpenProjectCreateTaskDialog;
